import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CurrencyBtc, Wallet } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success("Account created. Your account is funded and ready to trade.");
      navigate("/trade");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-screen flex bg-[#0A0E17]">
      <div className="hidden md:flex w-1/2 relative overflow-hidden border-r border-[#1F2937]">
        <div className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1760978631985-590e3b5f4057?crop=entropy&cs=srgb&fm=jpg&q=85')" }} />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0E17]/95 via-[#0A0E17]/70 to-[#0A0E17]" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#FCD535] flex items-center justify-center rounded-sm">
              <CurrencyBtc size={22} weight="bold" className="text-black" />
            </div>
          </div>
          <div className="space-y-6">
            <p className="overline">New trader</p>
            <h1 className="text-5xl font-bold leading-tight">Trade crypto &<br /><span className="text-[#0ECB81]">tokenized stocks</span></h1>
            <p className="text-[#9CA3AF] text-base max-w-md">Open positions on top crypto pairs and tokenized stocks (xStocks), use up to 100x leverage, and track every trade in your history.</p>
            <div className="flex items-center gap-2 text-sm text-[#FCD535]">
              <Wallet size={18} weight="bold" />
              <span>Wallet funded on signup</span>
            </div>
          </div>
          <div className="text-xs text-[#9CA3AF]">© 2015 Investment arena</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="register-form">
          <div className="space-y-2">
            <p className="overline">Create account</p>
            <h2 className="text-3xl font-semibold">Sign up</h2>
            <p className="text-sm text-[#9CA3AF]">Free forever. No real funds.</p>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="overline mb-1.5 block">Name</Label>
              <Input value={form.name} onChange={change("name")} required minLength={1}
                className="bg-[#0A0E17] border-[#1F2937] focus:border-[#FCD535] h-11 rounded-sm"
                data-testid="register-name-input" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Email</Label>
              <Input type="email" value={form.email} onChange={change("email")} required
                className="bg-[#0A0E17] border-[#1F2937] focus:border-[#FCD535] h-11 rounded-sm"
                data-testid="register-email-input" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Password (min 6)</Label>
              <Input type="password" value={form.password} onChange={change("password")} required minLength={6}
                className="bg-[#0A0E17] border-[#1F2937] focus:border-[#FCD535] h-11 rounded-sm"
                data-testid="register-password-input" />
            </div>
          </div>
          <Button type="submit" disabled={loading}
            className="w-full h-11 bg-[#FCD535] hover:bg-[#FCD535]/90 text-black font-semibold rounded-sm"
            data-testid="register-submit-button">
            {loading ? "Creating..." : "Create account"}
          </Button>
          <p className="text-sm text-center text-[#9CA3AF]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#FCD535] hover:underline" data-testid="login-link">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
